import React, { useCallback, useContext, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import api from 'api';
import { SUCCESS_MESSAGES } from 'request-responses';
import useCallbackRef from 'hooks/useCallbackRef';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import LeadsContext from '../../../../../../providers/Leads';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { Button, Loader } from 'ncoded-component-library';
import ListingContext from '../../../../../../providers/Listing/Listing.context';
import PreviewNbioModal from 'components/PreviewNbioModal';
import {
  BUYERS_ACTIVITY_TRACK_EVENTS,
  BuyersActivityTrackEvents,
} from 'models/Lead';

import './Nbio.styles.scss';

type NbioProps = {
  className?: string;
  addNewActivity: (eventType: BuyersActivityTrackEvents) => void;
};

const Nbio: React.FC<NbioProps> = (props) => {
  const { className, addNewActivity } = props;

  const [isLoading, setIsLoading] = useState(false);

  const { popServerError, popSuccess } = useContext(PopNotificationsContext);
  const { selectedLead, updateLead } = useContext(LeadsContext);
  const { areActionsDisabled } = useContext(ListingContext);

  const {
    _id: leadId,
    NBIO,
    isOfferAccepted,
    isOfferRejected,
  } = selectedLead || {};

  const [modal, modalRef] = useCallbackRef<ModalRef>(null);

  const classes = classNames('nbio', className);

  const { t } = useTranslation();

  const handleAcceptOffer = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data } = await api.portfolio.seller.acceptOffer(leadId);
      updateLead({ ...data, _id: leadId });
      popSuccess(t(SUCCESS_MESSAGES.OFFER_ACCEPTED));
      addNewActivity(BUYERS_ACTIVITY_TRACK_EVENTS.OFFER_ACCEPTED);
      modal?.close();
    } catch (e) {
      popServerError(e);
    } finally {
      setIsLoading(false);
    }
  }, [
    leadId,
    modal,
    addNewActivity,
    popServerError,
    popSuccess,
    t,
    updateLead,
  ]);

  const handleRejectOffer = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data } = await api.portfolio.seller.rejectOffer(leadId);
      updateLead({ ...data, _id: leadId });
      popSuccess(t(SUCCESS_MESSAGES.OFFER_REJECTED));
      addNewActivity(BUYERS_ACTIVITY_TRACK_EVENTS.OFFER_REJECTED);
      modal?.close();
    } catch (e) {
      popServerError(e);
    } finally {
      setIsLoading(false);
    }
  }, [
    leadId,
    modal,
    addNewActivity,
    popServerError,
    popSuccess,
    t,
    updateLead,
  ]);

  if (!NBIO || isOfferAccepted || isOfferRejected) return null;

  return (
    <>
      <Button
        className={classes}
        variant="outline"
        disabled={areActionsDisabled}
        onClick={() => {
          if (areActionsDisabled) return;
          modal?.open();
        }}
      >
        {t('viewOffer')}
      </Button>

      <PreviewNbioModal ref={modalRef} nbio={NBIO}>
        {isLoading && <Loader />}

        <div className="actions">
          <Button variant="outline" onClick={handleRejectOffer}>
            {t('reject')}
          </Button>
          <Button onClick={handleAcceptOffer}>{t('accept')}</Button>
        </div>
      </PreviewNbioModal>
    </>
  );
};

export default Nbio;
